import { useState, useContext, useEffect, useRef } from "react"
import AppContextObject from "../../../common/context/common"
import { formatDateShowMonth } from "../../../service/helper"

const CsvImport = () => {
    const context = useContext(AppContextObject)
    const fileInputRef = useRef(null)
    const [csvData, setCsvData] = useState("")
    const [loading, setLoading] = useState(false)
    const [importStatus, setImportStatus] = useState("")
    const [importResult, setImportResult] = useState(null)
    const [errors, setErrors] = useState([])

    const csvFormat = "schoolName, address, city, state, pincode, educationLevel, schoolType, category, totalStudents, requiredStudents, participatedStudents, initialApproval"

    const handleCsvDataChange = (e) => {
        setCsvData(e.target.value)
        // Clear errors when user starts typing
        if (errors.length > 0) {
            setErrors([])
        }
    }

    const handleFileUpload = (e) => {
        const file = e.target.files[0]
        if (file) {
            const reader = new FileReader()
            reader.onload = (event) => {
                setCsvData(event.target.result as string)
                // Clear errors when file is uploaded
                if (errors.length > 0) {
                    setErrors([])
                }
            }
            reader.readAsText(file)
        }
    }

    const validateCsvData = (data) => {
        const newErrors = []
        
        if (!data.trim()) {
            newErrors.push("CSV data is required")
            return newErrors
        }

        const lines = data.trim().split('\n')
        if (lines.length < 2) {
            newErrors.push("CSV data must contain at least a header row and one data row")
            return newErrors
        }

        const headers = lines[0].split(',').map(h => h.trim())
        const requiredHeaders = csvFormat.split(',').map(h => h.trim())
        const missingHeaders = requiredHeaders.filter(header => !headers.includes(header))
        
        if (missingHeaders.length > 0) {
            newErrors.push(`Missing required headers: ${missingHeaders.join(', ')}`)
        }

        // Validate data rows
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim()
            if (line) {
                const values = line.split(',').map(v => v.trim())
                if (values.length !== headers.length) {
                    newErrors.push(`Row ${i + 1}: Number of columns doesn't match headers`)
                }
                
                // Validate required fields
                const rowData: { [key: string]: string } = {}
                headers.forEach((header, index) => {
                    rowData[header] = values[index] || ""
                })
                
                if (!rowData.schoolName) {
                    newErrors.push(`Row ${i + 1}: School name is required`)
                }
                
                if (!rowData.address) {
                    newErrors.push(`Row ${i + 1}: Address is required`)
                }
                
                if (!rowData.city) {
                    newErrors.push(`Row ${i + 1}: City is required`)
                }
                
                if (!rowData.state) {
                    newErrors.push(`Row ${i + 1}: State is required`)
                }
            }
        }

        return newErrors
    }

    const importCsvData = async () => {
        // Clear previous errors
        setErrors([])
        
        // Validate CSV data
        const validationErrors = validateCsvData(csvData)
        if (validationErrors.length > 0) {
            setErrors(validationErrors)
            return
        }

        setLoading(true)
        setImportStatus("Importing CSV data...")

        try {
            // Parse CSV data
            const lines = csvData.trim().split('\n')
            const headers = lines[0].split(',').map(h => h.trim())
            const data = lines.slice(1).map(line => {
                const values = line.split(',').map(v => v.trim())
                const row = {}
                headers.forEach((header, index) => {
                    row[header] = values[index] || ""
                })
                return row
            })

            // Call API to import data
            const resp = await context.apis[context.user.role].importSchoolData(
                context.validator, 
                { csvData: data }, 
                {}
            )

            if (resp && resp.done) {
                setImportStatus("CSV data imported successfully!")
                setImportResult({
                    totalRecords: data.length,
                    successCount: resp.data?.successCount || data.length,
                    errorCount: resp.data?.errorCount || 0,
                    errors: resp.data?.errors || []
                })
            } else {
                setImportStatus("Failed to import CSV data. Please check the format and try again.")
            }
        } catch (error) {
            console.error("CSV import error:", error)
            setImportStatus("An error occurred while importing CSV data. Please try again.")
        }

        setLoading(false)
    }

    const clearData = () => {
        setCsvData("")
        setImportStatus("")
        setImportResult(null)
        setErrors([])
        // Clear the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    return (
        <div className="col-xl-10 col-lg-12 col-md-12 col-xs-12">
            <div className="rightPart noBack">
                <div className="row">
                    <div className="col-xs-12 col-lg-12 col-md-12 col-xs-12">
                        <div className="impressions-slider box-design csv-import-container">
                            <div className="inventory-overHead">
                                <h3 className="htx2 clearfix mb-10">CSV Import</h3>
                                <p className="ptx2">CSV Format: {csvFormat}</p>
                            </div>
                            
                            <div className="row mt-20">
                                <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12">
                                    <div className="forminpt">
                                        <label>Upload CSV File (Optional)</label>
                                        <input 
                                            ref={fileInputRef}
                                            type="file" 
                                            accept=".csv" 
                                            onChange={handleFileUpload}
                                        />
                                    </div>
                                </div>
                            </div>



                            <div className="row mt-20">
                                <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12">
                                    <div className="forminpt">
                                        <label>CSV Data</label>
                                        <textarea
                                            value={csvData}
                                            onChange={handleCsvDataChange}
                                            placeholder={`${csvFormat}\nTest School 1,123 Main St, Shimla, Himachal Pradesh, 171001, Primary, Government, Category A, 150, 120, 100, true\nTest School 2,456 Oak Ave, Dharamshala, Himachal Pradesh, 176215, Secondary, Private, Category B, 200, 180, 160, true`}
                                            rows={10}
                                            style={{ resize: "vertical", fontFamily: "'Courier New', monospace", fontSize: "12px", lineHeight: "1.4" }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-20">
                                <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12">
                                    <button 
                                        className={`btn btn-orange ${loading ? 'btn-disabled' : ''}`}
                                        onClick={importCsvData}
                                        disabled={loading}
                                    >
                                        {loading ? 'Importing...' : 'Import CSV Data'}
                                    </button>
                                    <button 
                                        className="btn btn-outline ml-10"
                                        onClick={clearData}
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            {importStatus && (
                                <div className="row mt-20">
                                    <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12">
                                        <div className={`valError ${importStatus.includes('successfully') ? 'text-success' : importStatus.includes('error') ? 'text-danger' : ''}`}>
                                            {importStatus}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {importResult && (
                                <div className="row mt-20">
                                    <div className="col-xl-12 col-lg-12 col-md-12 col-xs-12">
                                        <div className="inventorydetailsbox">
                                            <div className="inventory-overHead">
                                                <h3 className="htx2">Import Results</h3>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <div className="stat-item">
                                                        <h6>Total Records</h6>
                                                        <p>{importResult.totalRecords}</p>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="stat-item">
                                                        <h6>Successfully Imported</h6>
                                                        <p className="text-success">{importResult.successCount}</p>
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="stat-item">
                                                        <h6>Errors</h6>
                                                        <p className="text-danger">{importResult.errorCount}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {importResult.errors && importResult.errors.length > 0 && (
                                                <div className="mt-20">
                                                    <h6>Error Details:</h6>
                                                    <ul className="error-list">
                                                        {importResult.errors.map((error, index) => (
                                                            <li key={index} className="valError">
                                                                Row {error.row}: {error.message}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CsvImport 